/**
 * The parts of the Google sign-in flow that are not the button.
 *
 * ## Why they are not in the component file
 *
 * Two of them are read by the callback page, which means the button's file was exporting both a
 * component and plain functions. React Fast Refresh cannot patch a module like that: every edit to
 * the button remounted the page instead of updating it, taking any half-typed form with it. Moving
 * the helpers out is what makes the component file a component file again.
 */

/**
 * Where Google sends people back to.
 *
 * Three things have to agree on this string: the entry in the Google console, the route in
 * `App.tsx`, and the value handed to Google when the flow starts. The router reads it from here so
 * that only two of them can ever drift, and the one that can is the one a human checks by hand.
 */
export const GOOGLE_REDIRECT_PATH = '/auth/google/callback';

/** The absolute form. This is what Google is given, and what it is compared against on return. */
export function googleRedirectUri(): string {
  return `${window.location.origin}${GOOGLE_REDIRECT_PATH}`;
}

/** Survives the round trip to Google, which is the whole point of it. */
const STATE_KEY = 'iap.google.state';

/**
 * Stores the state the server minted, for the comparison on the way back.
 *
 * Returns false when the browser refuses storage, because the caller has to know: without a stored
 * state the return leg cannot be verified and the sign-in would be rejected there, several seconds
 * and one redirect too late to explain why.
 */
export function rememberGoogleState(state: string): boolean {
  try {
    sessionStorage.setItem(STATE_KEY, state);

    return true;
  } catch {
    return false;
  }
}

/** Reads and clears the stored state. One use, so a stale value cannot authorise a later return. */
export function takeStoredGoogleState(): string | null {
  try {
    const state = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);

    return state;
  } catch {
    return null;
  }
}
